// write by cursor
// 生成时间: 2025-07-29 16:45:55
// Token刷新管理器

import axios from 'axios';
import { notification } from 'antd';
import { gsT } from '../i18n';
import { getLocale } from '../storage/LocaleManager';
import { generateConfig } from '../utils'
import { API_URL_CONFIG_TYPE } from '../types';
/**
 * Token刷新管理器配置 - write by cursor
 */
export interface TokenManagerConfig {
  getAccessToken: () => string | null;
  getRefreshToken: () => string | null;
  setAccessToken: (token: string) => void;
  setRefreshToken: (token: string) => void;
  onAuthError?: () => void;
}

/**
 * 优雅的Token刷新管理器 - write by cursor
 */
export class TokenRefreshManager {
  private isRefreshing = false;
  private refreshPromise: Promise<string> | null = null;
  private debug = process.env.NODE_ENV === "development";
  private config: TokenManagerConfig;

  constructor(config: TokenManagerConfig) {
    this.config = config;
  }

  /**
   * 调试日志方法 - write by cursor
   */
  private log(...args: any[]): void {
    if (this.debug) {
      console.log("[TokenRefresh]", ...args);
    }
  }

  /**
   * 主要的token刷新方法 - write by cursor
   */
  async refreshToken(): Promise<string> {
    this.log("Token refresh requested, isRefreshing:", this.isRefreshing);
    
    const refreshTokenValue = this.config.getRefreshToken();
    if (!refreshTokenValue) {
      this.log("No refresh token available, redirecting to login");
      this.handleAuthError();
      throw new Error("No refresh token");
    }

    // 如果正在刷新，返回同一个Promise - write by cursor
    if (this.isRefreshing && this.refreshPromise) {
      this.log("Token refresh already in progress, awaiting existing promise");
      return this.refreshPromise;
    }

    // 设置刷新状态并创建刷新Promise - write by cursor
    this.isRefreshing = true;
    this.log("Starting token refresh...");

    this.refreshPromise = this._performRefresh(refreshTokenValue);
    
    try {
      const result = await this.refreshPromise;
      return result;
    } finally {
      // 无论成功失败都清理状态 - write by cursor
      this.isRefreshing = false;
      this.refreshPromise = null;
    }
  }

  /**
   * 执行实际的刷新操作 - write by cursor
   */
  private async _performRefresh(refreshTokenValue: string): Promise<string> {
    const { bot } = generateConfig() as API_URL_CONFIG_TYPE;
    try {
      // 刷新token请求，3秒超时 - write by cursor
      const refreshResponse = await axios.post(
        `${bot}/gas/api/v1alpha1/auth/token`,
        { refreshToken: refreshTokenValue },
        {
          timeout: 3000,
          headers: {
            "Content-Type": "application/json",
          },
          withCredentials: true,
        }
      );

      const { accessToken, refreshToken: newRefreshToken } = refreshResponse.data?.data;

      if (accessToken) {
        this.log("Token refresh successful");
        this.log("New token data:", {
          hasAccessToken: !!accessToken,
          hasRefreshToken: !!newRefreshToken,
        });

        // 保存新的token - write by cursor
        this.config.setAccessToken(accessToken);
        if (newRefreshToken) {
          this.config.setRefreshToken(newRefreshToken);
        }
        
        return accessToken;
      } else {
        throw new Error("Token refresh failed: Invalid response");
      }
    } catch (error: any) {
      this.log("Token refresh failed:", error.message);
      // 刷新失败时显示提示并退出登录 - write by cursor
      this.log("Refresh failed, logging out user");
      this.handleAuthError();
      throw error;
    }
  }

  /**
   * 处理认证错误 - write by cursor
   */
  private handleAuthError(): void {
    this.config.setAccessToken('');
    this.config.setRefreshToken('');
    
    if (this.config.onAuthError) {
      this.config.onAuthError();
    } else {
      const { bot } = generateConfig() as API_URL_CONFIG_TYPE;
      // 延迟100ms执行，主线程有后置的login接口根据sessionId获取新token过程，logout立马被login刷掉，存在sessionId无法被清除的情况
      setTimeout(() => {
        window.location.href = `${bot}/gas/api/v1alpha1/sso/logout?app=${encodeURIComponent(
          document.location.origin + document.location.pathname + '&locale=' + getLocale()
        )}`;
      }, 300);
    }
  }

  /**
   * 获取当前访问令牌 - write by cursor
   */
  getAccessToken(): string | null {
    return this.config.getAccessToken();
  }

  /**
   * 获取当前刷新令牌 - write by cursor
   */
  getRefreshToken(): string | null {
    return this.config.getRefreshToken();
  }

  /**
   * 销毁方法，清理资源 - write by cursor
   */
  destroy(): void {
    this.log("Destroying TokenRefreshManager...");
    this.isRefreshing = false;
    this.refreshPromise = null;
  }
} 